import {isSelectiveAiDomains} from 'common/HelperFunctions'
import {trackCreditWallEvent} from 'common/helpers/creditWallEventTracking'
import {
  FREEMIUM_TEACHER_LIMIT,
  FREEMIUM_TEACHER_LIMIT_MESSAGE,
  extractPlanNameFromAvailableServices,
  isFreemiumPlanName,
} from 'common/helpers/freemiumPlan'
import {openPlanUpgradeSelection} from 'common/helpers/openPlanUpgrade'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from 'setup'
import {getUserStatistics} from 'setup/redux/Actions/Dashboard/dashboard'

interface UseFreemiumTeacherLimitOptions {
  /**
   * Whether reaching the cap should raise the CreditsWall signal behind
   * lifecycle email E14b. Defaults to true — the teachers page wants it.
   *
   * The onboarding wizard passes false. A school typing its 10th teacher into
   * the wizard has not been refused anything; it is filling in the setup form,
   * and E14b ("you've hit your plan limit, upgrade") is both wrong and
   * expensive there. E14b carries priority 10, the highest in the matrix, so
   * the dispatcher's 23-hour frequency cap would cancel that school's E7/E8/E9
   * onboarding nudges (priority 30/40) as `priority_lost` — the school gets an
   * upsell instead of the help it was in the middle of needing.
   *
   * Only the emit is suppressed. `canAddTeacher`, the disabled add button and
   * the upgrade message are unchanged, so the cap still blocks in the wizard
   * exactly as it did before.
   */
  report?: boolean
}

export function useFreemiumTeacherLimit(
  teacherCount: number,
  options?: UseFreemiumTeacherLimitOptions
) {
  const dispatch = useDispatch()
  const shouldReport = options?.report !== false
  const isSelectiveAiDomain = isSelectiveAiDomains()
  const urddId = useSelector<RootState>((state: any) => state.userReducer.urddId) as string | null
  // See the reporting effect below.
  const limitReportedRef = useRef(false)

  const [planName, setPlanName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(isSelectiveAiDomain)
  const [isUpgrading, setIsUpgrading] = useState(false)

  useEffect(() => {
    if (!isSelectiveAiDomain) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    dispatch(
      getUserStatistics(
        (res: unknown) => {
          setPlanName(extractPlanNameFromAvailableServices(res))
          setIsLoading(false)
        },
        () => {
          setPlanName(null)
          setIsLoading(false)
        }
      )
    )
  }, [dispatch, isSelectiveAiDomain])

  const isFreemium = isFreemiumPlanName(planName)
  const applies = isSelectiveAiDomain && isFreemium
  const safeCount = Number.isFinite(teacherCount) && teacherCount >= 0 ? teacherCount : 0
  const isAtLimit = applies && safeCount >= FREEMIUM_TEACHER_LIMIT
  const canAddTeacher = !applies || safeCount < FREEMIUM_TEACHER_LIMIT

  // Report the teacher cap to the backend the moment it actually blocks the user.
  //
  // This wall is enforced entirely in the browser — plan name from
  // getUserStatistics, count from the caller — so no server ever learns it
  // happened. The one server-side teacher check (node-api
  // validateTeacherImportLimit) is reachable only from the excel-import
  // column-mapper, so a tenant who hits the cap on the teachers page or in the
  // onboarding wizard used to be blocked in total silence, and lifecycle email
  // E14b ("you've hit your plan limit → upgrade") could never fire for them.
  //
  // Reported from the hook rather than from its consumers so every caller that
  // opts in is covered by one emit. Opting OUT is per-consumer, via
  // options.report — see the interface above for why onboarding does.
  useEffect(() => {
    if (!isAtLimit) {
      // Back under the cap (a teacher was removed, or the plan was upgraded):
      // allow the next genuine block to report again. This is what lets a
      // school that trims its list and later grows back into the cap receive
      // E14b a second time, rather than one email ever.
      limitReportedRef.current = false
      return
    }
    if (!shouldReport) return
    if (limitReportedRef.current) return
    limitReportedRef.current = true
    trackCreditWallEvent(dispatch, urddId, 'plan_limit_hit', {
      entity: 'teacher',
      planName,
      limit: FREEMIUM_TEACHER_LIMIT,
      currentCount: safeCount,
      source: 'portal_freemium_teacher_limit',
    })
  }, [isAtLimit, shouldReport, dispatch, urddId, planName, safeCount])

  const openUpgrade = useCallback(() => {
    openPlanUpgradeSelection(dispatch, {
      onStart: () => setIsUpgrading(true),
      onComplete: () => setIsUpgrading(false),
      onError: () => setIsUpgrading(false),
    })
  }, [dispatch])

  return useMemo(
    () => ({
      planName,
      isFreemium,
      applies,
      isAtLimit,
      canAddTeacher,
      limit: FREEMIUM_TEACHER_LIMIT,
      teacherCount: safeCount,
      isLoading,
      isUpgrading,
      message: FREEMIUM_TEACHER_LIMIT_MESSAGE,
      openUpgrade,
    }),
    [
      applies,
      canAddTeacher,
      isAtLimit,
      isFreemium,
      isLoading,
      isUpgrading,
      openUpgrade,
      planName,
      safeCount,
    ]
  )
}
